import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Separator } from "./ui/separator";
import { Progress } from "./ui/progress";
import {
  FileText,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Clock,
  MessageSquare,
  PlusCircle,
  Send,
  Paperclip,
  AlertCircle,
  CheckCircle2,
  BarChart3,
  Target,
  DollarSign,
  Lightbulb,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
} from "lucide-react";
import { toast } from "sonner";

interface OutlookReview {
  id: string;
  title: string;
  advisor: {
    id: string;
    name: string;
    firm: string;
    avatar?: string;
  };
  analyst: {
    id: string;
    name: string;
    firm: string;
    avatar?: string;
  };
  status: "requested" | "in_progress" | "completed" | "archived";
  priority: "low" | "medium" | "high";
  createdDate: string;
  dueDate: string;
  lastUpdated: string;
  focusAreas: string[];
  clientContext?: string;
  messages: Message[];
  marketAnalysis?: MarketAnalysis;
  recommendations?: Recommendation[];
  attachments?: Attachment[];
}

interface Message {
  id: string;
  sender: "advisor" | "analyst";
  senderName: string;
  content: string;
  timestamp: string;
  type: "message" | "analysis" | "recommendation" | "question";
}

interface MarketAnalysis {
  economicOutlook: {
    gdpGrowth: string;
    inflation: string;
    interestRates: string;
    sentiment: "bullish" | "neutral" | "bearish";
  };
  sectorOutlook: {
    sector: string;
    rating: "overweight" | "neutral" | "underweight";
    rationale: string;
  }[];
  risks: {
    title: string;
    severity: "low" | "medium" | "high";
    description: string;
  }[];
}

interface Recommendation {
  id: string;
  type: "allocation" | "security" | "strategy";
  title: string;
  description: string;
  priority: "low" | "medium" | "high";
  status: "pending" | "accepted" | "rejected";
  expectedImpact?: string;
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadedBy: string;
  uploadedAt: string;
}

const sampleReviews: OutlookReview[] = [
  {
    id: "1",
    title: "Q4 2025 Portfolio Outlook Review",
    advisor: {
      id: "adv1",
      name: "Michael Torres",
      firm: "Torres Wealth Management",
      avatar: undefined,
    },
    analyst: {
      id: "ana1",
      name: "Sarah Chen",
      firm: "Chen Analytics",
      avatar: undefined,
    },
    status: "in_progress",
    priority: "high",
    createdDate: "2025-10-10",
    dueDate: "2025-10-25",
    lastUpdated: "2025-10-16 09:30 AM",
    focusAreas: ["Technology Sector", "Interest Rate Impact", "Portfolio Rebalancing"],
    clientContext: "Reviewing portfolio strategy for 15 high-net-worth clients ($25M AUM total). Clients are concerned about tech sector concentration and rising interest rates.",
    messages: [
      {
        id: "m1",
        sender: "advisor",
        senderName: "Michael Torres",
        content: "Hi Sarah, I'd like to get your perspective on the technology sector outlook for Q4. My clients are heavily weighted in tech (40%+ allocation) and are getting nervous about valuations. What's your take on the sector heading into year-end?",
        timestamp: "2025-10-10 10:15 AM",
        type: "question",
      },
      {
        id: "m2",
        sender: "analyst",
        senderName: "Sarah Chen",
        content: "Thanks for reaching out, Michael. I've reviewed the sector dynamics and I think your clients' concerns are valid. Tech valuations are stretched, particularly in AI-related stocks. However, fundamentals remain strong. Let me prepare a detailed analysis covering earnings growth, valuation metrics, and interest rate sensitivity.",
        timestamp: "2025-10-10 02:30 PM",
        type: "message",
      },
      {
        id: "m3",
        sender: "analyst",
        senderName: "Sarah Chen",
        content: "I've completed my analysis. Here are the key findings:\n\n📊 **Tech Sector Outlook**: Moderately Bullish\n\n**Key Points:**\n- AI infrastructure spending remains robust (+35% YoY)\n- Cloud computing growth stabilizing at 20-25%\n- Semiconductor cycle showing early recovery signs\n- Valuation: P/E ratios 15% above 10-year average\n\n**Recommendation**: Maintain exposure but consider trimming mega-cap positions and rotating into value-oriented tech (semiconductors, enterprise software with recurring revenue).\n\nI'll send over detailed sector analysis shortly.",
        timestamp: "2025-10-12 11:45 AM",
        type: "analysis",
      },
      {
        id: "m4",
        sender: "advisor",
        senderName: "Michael Torres",
        content: "This is excellent, Sarah. The semiconductor rotation idea is particularly interesting. Can you provide specific ticker recommendations with target allocations? Also, what's your view on the interest rate environment's impact on tech multiples?",
        timestamp: "2025-10-12 03:20 PM",
        type: "question",
      },
      {
        id: "m5",
        sender: "analyst",
        senderName: "Sarah Chen",
        content: "Absolutely. On interest rates: Fed is likely done hiking, but rates staying 'higher for longer' will pressure growth stocks. I'd recommend:\n\n1. **Trim**: High-multiple SaaS stocks (>10x sales)\n2. **Add**: NVDA, AMD, AVGO (semiconductor leadership)\n3. **Target Allocation**: Reduce tech from 40% to 32-35%\n4. **Reallocate**: 5% to dividend-paying sectors (utilities, consumer staples)\n\nI'm uploading a detailed report with specific recommendations and risk scenarios.",
        timestamp: "2025-10-14 09:15 AM",
        type: "recommendation",
      },
      {
        id: "m6",
        sender: "advisor",
        senderName: "Michael Torres",
        content: "Perfect timing - I have client reviews next week. This gives me exactly what I need. Can we schedule a 30-min call on Friday to discuss implementation strategy? I want to make sure I'm explaining the rationale correctly to clients.",
        timestamp: "2025-10-14 11:30 AM",
        type: "message",
      },
    ],
    marketAnalysis: {
      economicOutlook: {
        gdpGrowth: "2.1% - 2.5%",
        inflation: "3.2% (trending toward 2.5%)",
        interestRates: "5.25% - 5.50% (holding)",
        sentiment: "neutral",
      },
      sectorOutlook: [
        {
          sector: "Technology",
          rating: "neutral",
          rationale: "Strong fundamentals offset by valuation concerns. AI infrastructure spending positive, but consumer tech weakening.",
        },
        {
          sector: "Financials",
          rating: "overweight",
          rationale: "Benefiting from higher-for-longer rates. Strong loan growth and net interest margins.",
        },
        {
          sector: "Healthcare",
          rating: "overweight",
          rationale: "Defensive characteristics attractive. GLP-1 obesity drugs creating new growth opportunities.",
        },
        {
          sector: "Energy",
          rating: "underweight",
          rationale: "Oil prices stabilizing but demand concerns persist. OPEC+ production discipline uncertain.",
        },
      ],
      risks: [
        {
          title: "Geopolitical Tensions",
          severity: "medium",
          description: "Middle East conflicts and China-Taiwan relations pose market volatility risks.",
        },
        {
          title: "Stubborn Inflation",
          severity: "medium",
          description: "Core inflation remaining above Fed target could force additional rate hikes.",
        },
        {
          title: "Tech Sector Correction",
          severity: "high",
          description: "Concentration in mega-cap tech creates portfolio vulnerability to sector rotation.",
        },
      ],
    },
    recommendations: [
      {
        id: "r1",
        type: "allocation",
        title: "Reduce Technology Allocation",
        description: "Trim technology sector from 40% to 32-35% to reduce concentration risk and take profits on overvalued positions.",
        priority: "high",
        status: "pending",
        expectedImpact: "Reduce portfolio volatility by ~8-10%",
      },
      {
        id: "r2",
        type: "allocation",
        title: "Increase Financials Exposure",
        description: "Add 5% allocation to financials sector, focusing on regional banks with strong deposit franchises and commercial real estate lenders.",
        priority: "medium",
        status: "pending",
        expectedImpact: "Enhance income generation, add defensive characteristics",
      },
      {
        id: "r3",
        type: "security",
        title: "Semiconductor Rotation Strategy",
        description: "Within tech allocation, rotate from high-multiple SaaS to semiconductor leaders (NVDA, AMD, AVGO) to maintain tech exposure with better valuations.",
        priority: "high",
        status: "pending",
        expectedImpact: "Maintain growth potential while improving risk/reward",
      },
      {
        id: "r4",
        type: "strategy",
        title: "Implement Tax-Loss Harvesting",
        description: "Identify underperforming tech positions for tax-loss harvesting while maintaining sector exposure through similar holdings.",
        priority: "medium",
        status: "pending",
        expectedImpact: "Generate $50K-$75K in tax savings across client base",
      },
    ],
    attachments: [
      {
        id: "att1",
        name: "Q4_2025_Tech_Sector_Analysis.pdf",
        type: "PDF",
        size: "2.3 MB",
        uploadedBy: "Sarah Chen",
        uploadedAt: "2025-10-14 09:15 AM",
      },
      {
        id: "att2",
        name: "Portfolio_Rebalancing_Recommendations.xlsx",
        type: "Excel",
        size: "1.1 MB",
        uploadedBy: "Sarah Chen",
        uploadedAt: "2025-10-14 09:18 AM",
      },
      {
        id: "att3",
        name: "Client_Portfolio_Summary.pdf",
        type: "PDF",
        size: "890 KB",
        uploadedBy: "Michael Torres",
        uploadedAt: "2025-10-10 10:20 AM",
      },
    ],
  },
  {
    id: "2",
    title: "Interest Rate Strategy Review",
    advisor: {
      id: "adv2",
      name: "Jennifer Martinez",
      firm: "Martinez Financial Advisors",
      avatar: undefined,
    },
    analyst: {
      id: "ana2",
      name: "Alex Thompson",
      firm: "Thompson Research Group",
      avatar: undefined,
    },
    status: "requested",
    priority: "medium",
    createdDate: "2025-10-15",
    dueDate: "2025-10-30",
    lastUpdated: "2025-10-15 02:45 PM",
    focusAreas: ["Fixed Income Strategy", "Rate Sensitivity", "Duration Management"],
    clientContext: "Need guidance on fixed income positioning given current rate environment. Clients asking about bond duration and credit quality trade-offs.",
    messages: [
      {
        id: "m1",
        sender: "advisor",
        senderName: "Jennifer Martinez",
        content: "Hi Alex, I need your expertise on fixed income strategy. With rates potentially at peak, should we be extending duration in client portfolios? Current average duration is 4.5 years. Clients range from conservative to moderate risk tolerance.",
        timestamp: "2025-10-15 02:45 PM",
        type: "question",
      },
    ],
    recommendations: [],
    attachments: [],
  },
];

interface FinancialOutlookReviewProps {
  userRole: "advisor" | "analyst";
}

export function FinancialOutlookReview({ userRole }: FinancialOutlookReviewProps) {
  const [reviews, setReviews] = useState<OutlookReview[]>(sampleReviews);
  const [selectedReview, setSelectedReview] = useState<OutlookReview | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);

  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedReview) return;

    const message: Message = {
      id: `m${Date.now()}`,
      sender: userRole,
      senderName: userRole?.toLowerCase() === "advisor" ? "Michael Torres" : "Sarah Chen",
      content: newMessage,
      timestamp: new Date().toLocaleString(),
      type: "message",
    };

    const updatedReview = {
      ...selectedReview,
      messages: [...selectedReview.messages, message],
      lastUpdated: new Date().toLocaleString(),
    };

    setReviews(reviews.map((r) => (r.id === selectedReview.id ? updatedReview : r)));
    setSelectedReview(updatedReview);
    setNewMessage("");
    toast.success("Message sent");
  };

  const handleRequestReview = () => {
    toast.success("Outlook review request sent to analyst");
    setIsRequestDialogOpen(false);
  };

  const handleUpdateRecommendation = (
    reviewId: string,
    recommendationId: string,
    newStatus: "pending" | "accepted" | "rejected"
  ) => {
    const updatedReviews = reviews.map((review) => {
      if (review.id === reviewId && review.recommendations) {
        return {
          ...review,
          recommendations: review.recommendations.map((rec) =>
            rec.id === recommendationId ? { ...rec, status: newStatus } : rec
          ),
        };
      }
      return review;
    });
    setReviews(updatedReviews);
    if (selectedReview?.id === reviewId) {
      setSelectedReview(updatedReviews.find((r) => r.id === reviewId) || null);
    }
    toast.success(`Recommendation ${newStatus}`);
  };

  const getStatusColor = (status: OutlookReview["status"]) => {
    switch (status) {
      case "requested":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "in_progress":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
      case "completed":
        return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "archived":
        return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "medium":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300";
      case "low":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
    }
  };

  const activeReviews = reviews.filter((r) => r.status !== "archived");
  const archivedReviews = reviews.filter((r) => r.status === "archived");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">Financial Outlook Reviews</h2>
          <p className="text-muted-foreground">
            {userRole?.toLowerCase() === "advisor"
              ? "Collaborate with analysts on portfolio strategy and market outlook"
              : "Provide market insights and recommendations to advisors"}
          </p>
        </div>
        {userRole?.toLowerCase() === "advisor" && (
          <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <PlusCircle className="mr-2 h-4 w-4" />
                Request Outlook Review
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Request Financial Outlook Review</DialogTitle>
                <DialogDescription>
                  Request market analysis and portfolio recommendations from an analyst
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Review Title</Label>
                  <Input placeholder="e.g., Q4 2025 Portfolio Strategy Review" />
                </div>
                <div className="space-y-2">
                  <Label>Select Analyst</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an analyst" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ana1">Sarah Chen - Chen Analytics</SelectItem>
                      <SelectItem value="ana2">Alex Thompson - Thompson Research Group</SelectItem>
                      <SelectItem value="ana3">Maya Patel - Patel ESG Research</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select defaultValue="medium">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input type="date" />
                </div>
                <div className="space-y-2">
                  <Label>Focus Areas</Label>
                  <Input placeholder="e.g., Technology Sector, Interest Rates, ESG" />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated areas of focus
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Client Context</Label>
                  <Textarea
                    placeholder="Provide context about your clients, their concerns, current portfolio allocation, or specific questions..."
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleRequestReview}>Send Request</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active Reviews</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{activeReviews.length}</div>
            <p className="text-xs text-muted-foreground">Ongoing collaborations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              {reviews.filter((r) => r.status === "in_progress").length}
            </div>
            <p className="text-xs text-muted-foreground">Active discussions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Pending Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              {reviews.filter((r) => r.status === "requested").length}
            </div>
            <p className="text-xs text-muted-foreground">Awaiting response</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              {reviews.filter((r) => r.status === "completed").length}
            </div>
            <p className="text-xs text-muted-foreground">Finished reviews</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Reviews List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Outlook Reviews</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Tabs defaultValue="active">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="active">Active</TabsTrigger>
                  <TabsTrigger value="archived">Archived</TabsTrigger>
                </TabsList>
                <TabsContent value="active" className="space-y-3 mt-3">
                  {activeReviews.map((review) => (
                    <div
                      key={review.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-muted/50 ${
                        selectedReview?.id === review.id ? "bg-muted" : ""
                      }`}
                      onClick={() => setSelectedReview(review)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium line-clamp-2">{review.title}</h4>
                        <Badge className={getPriorityColor(review.priority)} variant="secondary">
                          {review.priority}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Users className="h-3 w-3" />
                          <span className="text-xs">
                            {review.advisor.name} ↔ {review.analyst.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(review.status)} variant="secondary">
                            {review.status.replace("_", " ")}
                          </Badge>
                          {review.messages.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MessageSquare className="h-3 w-3" />
                              {review.messages.length}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Due: {new Date(review.dueDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  {activeReviews.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No active reviews</p>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="archived" className="mt-3">
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No archived reviews</p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Review Details */}
        <div className="lg:col-span-2">
          {selectedReview ? (
            <Tabs defaultValue="discussion" className="space-y-4">
              <TabsList>
                <TabsTrigger value="discussion">Discussion</TabsTrigger>
                <TabsTrigger value="analysis">Market Analysis</TabsTrigger>
                <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
                <TabsTrigger value="attachments">Attachments</TabsTrigger>
              </TabsList>

              {/* Discussion Tab */}
              <TabsContent value="discussion" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle>{selectedReview.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={getStatusColor(selectedReview.status)}>
                            {selectedReview.status.replace("_", " ")}
                          </Badge>
                          <Badge className={getPriorityColor(selectedReview.priority)}>
                            {selectedReview.priority} priority
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Participants */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {selectedReview.advisor.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{selectedReview.advisor.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedReview.advisor.firm}
                          </p>
                        </div>
                      </div>
                      <span className="text-muted-foreground">↔</span>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {selectedReview.analyst.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium">{selectedReview.analyst.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedReview.analyst.firm}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Focus Areas */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Focus Areas</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedReview.focusAreas.map((area, idx) => (
                          <Badge key={idx} variant="outline">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Client Context */}
                    {selectedReview.clientContext && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Client Context</Label>
                        <p className="text-sm mt-1">{selectedReview.clientContext}</p>
                      </div>
                    )}

                    {/* Timeline */}
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">Created</Label>
                        <p className="font-medium">
                          {new Date(selectedReview.createdDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Due Date</Label>
                        <p className="font-medium">
                          {new Date(selectedReview.dueDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Last Updated</Label>
                        <p className="font-medium">{selectedReview.lastUpdated}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Messages */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Discussion Thread
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {selectedReview.messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex gap-3 ${
                            message.sender?.toLowerCase() === userRole?.toLowerCase() ? "flex-row-reverse" : ""
                          }`}
                        >
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>
                              {message.senderName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div
                            className={`flex-1 space-y-1 ${
                              message.sender?.toLowerCase() === userRole?.toLowerCase() ? "items-end" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{message.senderName}</span>
                              <span className="text-xs text-muted-foreground">
                                {message.timestamp}
                              </span>
                              {message.type !== "message" && (
                                <Badge variant="outline" className="text-xs">
                                  {message.type}
                                </Badge>
                              )}
                            </div>
                            <div
                              className={`p-3 rounded-lg text-sm whitespace-pre-line ${
                                message.sender?.toLowerCase() === userRole?.toLowerCase()
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted"
                              }`}
                            >
                              {message.content}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Separator />

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        rows={3}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            handleSendMessage();
                          }
                        }}
                      />
                      <div className="flex flex-col gap-2">
                        <Button variant="outline" size="icon">
                          <Paperclip className="h-4 w-4" />
                        </Button>
                        <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Market Analysis Tab */}
              <TabsContent value="analysis" className="space-y-4">
                {selectedReview.marketAnalysis ? (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <BarChart3 className="h-5 w-5" />
                          Economic Outlook
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground">GDP Growth</Label>
                            <p className="text-lg font-medium">
                              {selectedReview.marketAnalysis.economicOutlook.gdpGrowth}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Inflation</Label>
                            <p className="text-lg font-medium">
                              {selectedReview.marketAnalysis.economicOutlook.inflation}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Interest Rates</Label>
                            <p className="text-lg font-medium">
                              {selectedReview.marketAnalysis.economicOutlook.interestRates}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Sentiment</Label>
                            <Badge
                              className={
                                selectedReview.marketAnalysis.economicOutlook.sentiment ===
                                "bullish"
                                  ? "bg-green-100 text-green-700"
                                  : selectedReview.marketAnalysis.economicOutlook.sentiment ===
                                    "bearish"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                              }
                            >
                              {selectedReview.marketAnalysis.economicOutlook.sentiment}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Sector Outlook</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {selectedReview.marketAnalysis.sectorOutlook.map((sector, idx) => (
                          <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{sector.sector}</span>
                              <Badge
                                className={
                                  sector.rating === "overweight"
                                    ? "bg-green-100 text-green-700"
                                    : sector.rating === "underweight"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-gray-100 text-gray-700"
                                }
                              >
                                {sector.rating === "overweight" && (
                                  <TrendingUp className="h-3 w-3 mr-1" />
                                )}
                                {sector.rating === "underweight" && (
                                  <TrendingDown className="h-3 w-3 mr-1" />
                                )}
                                {sector.rating}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{sector.rationale}</p>
                            {idx < selectedReview.marketAnalysis.sectorOutlook.length - 1 && (
                              <Separator />
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5" />
                          Key Risks
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {selectedReview.marketAnalysis.risks.map((risk, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                            <AlertCircle
                              className={`h-5 w-5 mt-0.5 ${
                                risk.severity === "high"
                                  ? "text-red-500"
                                  : risk.severity === "medium"
                                  ? "text-orange-500"
                                  : "text-yellow-500"
                              }`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{risk.title}</span>
                                <Badge
                                  variant="outline"
                                  className={
                                    risk.severity === "high"
                                      ? "border-red-500 text-red-700"
                                      : risk.severity === "medium"
                                      ? "border-orange-500 text-orange-700"
                                      : "border-yellow-500 text-yellow-700"
                                  }
                                >
                                  {risk.severity} severity
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{risk.description}</p>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Market analysis not yet available</p>
                      <p className="text-sm">Analyst will provide analysis soon</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Recommendations Tab */}
              <TabsContent value="recommendations" className="space-y-4">
                {selectedReview.recommendations && selectedReview.recommendations.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Action Items & Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedReview.recommendations.map((rec) => (
                        <div key={rec.id} className="p-4 border rounded-lg space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline">{rec.type}</Badge>
                                <Badge
                                  className={
                                    rec.priority === "high"
                                      ? "bg-red-100 text-red-700"
                                      : rec.priority === "medium"
                                      ? "bg-orange-100 text-orange-700"
                                      : "bg-blue-100 text-blue-700"
                                  }
                                >
                                  {rec.priority} priority
                                </Badge>
                                <Badge
                                  className={
                                    rec.status === "accepted"
                                      ? "bg-green-100 text-green-700"
                                      : rec.status === "rejected"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-gray-100 text-gray-700"
                                  }
                                >
                                  {rec.status}
                                </Badge>
                              </div>
                              <h4 className="font-medium mb-1">{rec.title}</h4>
                              <p className="text-sm text-muted-foreground mb-2">
                                {rec.description}
                              </p>
                              {rec.expectedImpact && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Lightbulb className="h-4 w-4 text-yellow-600" />
                                  <span className="text-muted-foreground">
                                    Expected Impact: {rec.expectedImpact}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {userRole?.toLowerCase() === "advisor" && rec.status === "pending" && (
                            <div className="flex gap-2 pt-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  handleUpdateRecommendation(
                                    selectedReview.id,
                                    rec.id,
                                    "accepted"
                                  )
                                }
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  handleUpdateRecommendation(
                                    selectedReview.id,
                                    rec.id,
                                    "rejected"
                                  )
                                }
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No recommendations yet</p>
                      <p className="text-sm">Recommendations will appear here as discussion progresses</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Attachments Tab */}
              <TabsContent value="attachments" className="space-y-4">
                {selectedReview.attachments && selectedReview.attachments.length > 0 ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Paperclip className="h-5 w-5" />
                        Shared Documents
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedReview.attachments.map((attachment) => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{attachment.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {attachment.type} • {attachment.size} • Uploaded by{" "}
                                  {attachment.uploadedBy}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {attachment.uploadedAt}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <Paperclip className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No attachments</p>
                      <p className="text-sm">Documents and reports will appear here</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <Card>
              <CardContent className="py-20 text-center text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a review to view details</p>
                <p className="text-sm">
                  Choose from the list or request a new outlook review
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
